import { useEffect } from 'react';

import { useGetTournamentSlotsQuery } from '@/services/qduelsApi';
import type { QduelsParty, QduelsTournamentSummary } from '@/types/qduels';

/** One spectatable match, flattened out of a tournament's slots. */
export interface LiveMatch {
  tournamentId: string;
  tournamentName: string;
  kind: QduelsTournamentSummary['kind'];
  slot: number;
  round: number;
  a: QduelsParty | null;
  b: QduelsParty | null;
  spectatorAurl: string | null;
  spectatorBurl: string | null;
}

interface Props {
  tournament: QduelsTournamentSummary;
  /** Stable callback — report this tournament's live matches up to the parent. */
  onLive: (tournamentId: string, matches: LiveMatch[]) => void;
}

/**
 * Data-only child of `LiveMatchesPanel`: fetches ONE in-progress tournament's
 * current-round slots, filters the slots that carry a spectator link (present
 * only while their match is `inProgress`), and reports the resulting matches up.
 * Renders nothing — the parent owns the flat list + empty state.
 *
 * Rendering one of these per in-progress tournament keeps each RTK Query hook
 * call static (no hooks-in-a-loop), and isolates a per-tournament fetch failure.
 * The current round is where the live match usually sits; a bye-lagged
 * `currentRound` on an under-filled bracket can hide it (accepted for now).
 */
export function LiveTournamentSlots({ tournament, onLive }: Props) {
  const { data } = useGetTournamentSlotsQuery(
    { tournamentId: tournament.tournamentId, round: tournament.currentRound, limit: 50 },
    { pollingInterval: 15000, refetchOnFocus: true },
  );

  const { tournamentId, name, kind } = tournament;

  useEffect(() => {
    const matches: LiveMatch[] = (data?.slots ?? [])
      .filter((s) => s.spectatorAurl || s.spectatorBurl)
      .map((s) => ({
        tournamentId,
        tournamentName: name,
        kind,
        slot: s.slot,
        round: s.round,
        a: s.a,
        b: s.b,
        spectatorAurl: s.spectatorAurl,
        spectatorBurl: s.spectatorBurl,
      }));
    onLive(tournamentId, matches);
  }, [data, tournamentId, name, kind, onLive]);

  return null;
}
